import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import SettingsService from '@/services/SettingsService'
import ProjectRichTextEditor, { isRichTextEmpty } from '../common/ProjectRichTextEditor'

const OPIS_GUIDANCE_KEYS = [
	'confirmations',
	'invoiceEntity',
	'evaluationSurvey',
	'scenario',
	'otherInfo',
]

export default function ProjectCommonFieldsSection({
	opis = '',
	onSave,
	saving = false,
}) {
	const { t } = useTranslation()
	const [draftOpis, setDraftOpis] = useState(opis || '')
	const [editorKey, setEditorKey] = useState(0)
	const [schemaAction, setSchemaAction] = useState(null)

	const handleSave = async () => {
		if (typeof onSave !== 'function') return
		await onSave({ opis: draftOpis })
	}

	const handleApplySchema = async () => {
		setSchemaAction('apply')
		const result = await SettingsService.getProjektOpisSchemat()
		setSchemaAction(null)

		if (result.error) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error,
				'error'
			)
			return
		}

		const savedOpis = `${result.data?.opis || ''}`
		if (isRichTextEmpty(savedOpis)) {
			await Swal.fire(
				t('notifications.infoTitle', { defaultValue: 'Informacja' }),
				t('projects.details.messages.schemaEmpty', {
					defaultValue: 'Brak zapisanego schematu opisu.',
				}),
				'info'
			)
			return
		}

		setDraftOpis(savedOpis)
		setEditorKey(key => key + 1)
	}

	const handleSaveSchema = async () => {
		setSchemaAction('save')
		const result = await SettingsService.setProjektOpisSchemat({ opis: draftOpis })
		setSchemaAction(null)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('projects.details.messages.schemaSaveFailed', {
					defaultValue: 'Nie udało się zapisać schematu opisu.',
				}),
				'error'
			)
			return
		}

		await Swal.fire(
			t('notifications.successTitle'),
			t('projects.details.messages.schemaSaved', {
				defaultValue: 'Zapisano schemat opisu.',
			}),
			'success'
		)
	}

	return (
		<div className="mt-6 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<h2 className="text-sm font-semibold text-gray-900">
				{t('projects.details.sections.commonFields', { defaultValue: 'Opis' })}
			</h2>
			<div className="mt-4 space-y-4">
				<div className="rounded-md border border-amber-100 bg-amber-50 px-4 py-3">
					<p className="text-sm font-medium text-amber-900">
						{t('projects.details.opisGuidance.title', {
							defaultValue: 'W opisie warto uwzględnić:',
						})}
					</p>
					<ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-amber-900/90">
						{OPIS_GUIDANCE_KEYS.map(key => (
							<li key={key}>
								{t(`projects.details.opisGuidance.items.${key}`)}
							</li>
						))}
					</ol>
				</div>

				<div className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">
						{t('projects.form.fields.description', { defaultValue: 'Opis' })}
					</span>
					<ProjectRichTextEditor
						key={editorKey}
						value={draftOpis}
						onChange={setDraftOpis}
						placeholder={t('projects.form.placeholders.description', {
							defaultValue: 'Wpisz opis projektu...',
						})}
					/>
				</div>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							disabled={saving || schemaAction !== null}
							onClick={handleApplySchema}
							className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
						>
							{schemaAction === 'apply'
								? t('projects.edit.submitting')
								: t('projects.details.actions.applySchema', { defaultValue: 'Schemat' })}
						</button>
						<button
							type="button"
							disabled={saving || schemaAction !== null}
							onClick={handleSaveSchema}
							className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
						>
							{schemaAction === 'save'
								? t('projects.edit.submitting')
								: t('projects.details.actions.saveSchema', { defaultValue: 'Zapisz schemat' })}
						</button>
					</div>
					<button
						type="button"
						disabled={saving || schemaAction !== null}
						onClick={handleSave}
						className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
					>
						{saving
							? t('projects.edit.submitting')
							: t('projects.details.actions.saveCommon', { defaultValue: 'Zapisz opis' })}
					</button>
				</div>
			</div>
		</div>
	)
}
